<template>
    <div class="automation-template-card">
        <header class="automation-template-card__header">
            <template v-if="isLoading">
                <div class="automation-template-card__header-skeleton">
                    <CompassSkeleton v-for="skeleton in 3" :key="skeleton" variant="square" />
                </div>
            </template>
            <template v-else>
                <div class="automation-template-card__header-content">
                    <AutomationPieces :pieces="template.pieces" />
                    <CompassTooltip
                        v-if="template.isPopular"
                        :content="translations.popular"
                        :arrow="false"
                        class="automation-template-card__tag-tooltip"
                    >
                        <CompassTag
                            color="yellow"
                            size="small"
                            class="automation-template-card__tag"
                        >
                            {{ translations.labelFlag }}
                        </CompassTag>
                    </CompassTooltip>
                </div>
            </template>
        </header>
        <template v-if="isLoading">
            <CompassSkeleton class="automation-template-card__skeleton-half" />
            <div class="automation-template-card__content-skeleton">
                <CompassSkeleton v-for="skeleton in 2" :key="skeleton" />
            </div>
        </template>
        <template v-else>
            <div class="automation-template-card__content">
                <CompassTooltip
                    :content="template.title"
                    :arrow="false"
                    :disabled="!isTitleTruncated"
                    :delay="{ show: 1000, hide: 0 }"
                >
                    <p
                        ref="titleElement"
                        class="automation-template-card__title typographies-paragraph-medium-semibold"
                        @mouseenter="checkTitleTruncation"
                    >
                        {{ template.title }}
                    </p>
                </CompassTooltip>
                <CompassTooltip
                    :content="template.description"
                    :arrow="false"
                    :disabled="!isDescriptionTruncated"
                    :delay="{ show: 1000, hide: 0 }"
                >
                    <p
                        ref="descriptionElement"
                        class="automation-template-card__description typographies-paragraph-medium-regular"
                        @mouseenter="checkDescriptionTruncation"
                    >
                        {{ template.description }}
                    </p>
                </CompassTooltip>
            </div>
        </template>
        <footer class="automation-template-card__footer">
            <template v-if="isLoading">
                <CompassSkeleton class="automation-template-card__skeleton-half" />
            </template>
            <template v-else>
                <CompassButton
                    :label="translations.useTemplate"
                    type="tertiary"
                    :loading="isLoading"
                    class="automation-template-card__use-template-button"
                    @click="handleCreateAutomationWithTemplate"
                />
            </template>
        </footer>
    </div>
</template>

<script>
import AutomationPieces from 'vcomponents/automation/AutomationPieces.vue';
import { useAutomationTemplate } from 'api/automation/automationService.js';

import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');
const { mapActions: mapActionsPopupPanel } = createNamespacedHelpers('popupPanel');

export default {
    components: {
        AutomationPieces,
    },
    props: {
        template: {
            type: Object,
            required: true,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            translations: {
                labelFlag: '🔥',
                popular: this.$t.__('common.labels.popular'),
                useTemplate: this.$t.__('automation.AutomationTemplateCard.useTemplate'),
            },
            isTitleTruncated: false,
            isDescriptionTruncated: false,
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),
    },
    methods: {
        ...mapActionsPopupPanel(['open']),
        checkTitleTruncation() {
            this.$nextTick(() => {
                const element = this.$refs.titleElement;
                if (element) {
                    this.isTitleTruncated = element.scrollHeight > element.clientHeight;
                }
            });
        },
        checkDescriptionTruncation() {
            this.$nextTick(() => {
                const element = this.$refs.descriptionElement;
                if (element) {
                    this.isDescriptionTruncated = element.scrollHeight > element.clientHeight;
                }
            });
        },
        handleCreateAutomationWithTemplate() {
            if (this.template.isWizard) {
                this.open({
                    key: 'automationWizardPanel',
                    props: {
                        template: this.template,
                    },
                });
            } else {
                useAutomationTemplate(this.template.id)
                    .then((response) => {
                        this.$router.push({
                            name: 'automations.flow.builder',
                            params: { id: response.data.id },
                        });
                    })
                    .catch((error) => {
                        this.errorsHandler.logException(error);
                        this.$pushToast({
                            type: 'error',
                            close: true,
                            text: this.$t.__('common.labels.toast.error'),
                        });
                    });
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-template-card {
    height: 276px;
    position: relative;
    border-radius: 10px;
    padding: var(--spacing-3);
    background: var(--colors-common-background-default);
    box-shadow: var(--elevation-1);
    transition: all 0.2s ease-in-out;
    &:hover {
        transform: translateY(-4px);
    }

    &__skeleton {
        &-half {
            width: 50%;
            margin: 0 0 var(--spacing-5);
        }
    }

    &__header {
        height: 32px;
        display: flex;
        align-items: center;
        margin: 0 0 var(--spacing-3);

        &-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
        }

        &-skeleton {
            display: flex;
            gap: var(--spacing-0);
        }
    }
    &__tag {
        justify-content: center;
        width: 28px;
        height: 20px;
        cursor: default;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-1);

        &-skeleton {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-0);
        }
    }
    &__title {
        margin: 0;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    &__description {
        margin: 0;
        color: var(--colors-common-text-subtle-default);
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 4;
        line-clamp: 4;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__footer {
        position: absolute;
        width: calc(100% - 32px);
        bottom: var(--spacing-3);

        &:has(.automation-template-card__skeleton-half) {
            width: 50%;
            bottom: 0;
        }
    }
    &__use-template-button {
        width: 100%;
    }
}
</style>
